<template>
  <section class="booking">
    <TransitionRouterView @subview-event="__handleSubviewEvent"></TransitionRouterView>
  </section>
</template>

<script>
import TransitionRouterView from '@/containers/TransitionRouterView'
import flowController from '@/mixins/flowController'
import routes from '@/router/routes'

export default {
  mixins: [flowController],
  components: { TransitionRouterView },
  methods: {
    getSubviewEventStrategy() {
      return {
        'step1-complete': this.handleStep1Complete,
        'step2-complete': this.handleStep2Complete
      }
    },
    handleStep1Complete(event) {
      sessionStorage.setItem('booking/temp-booking', JSON.stringify(event.data))

      this.$router.push({
        name: routes.BOOKING_2
      })
    },
    handleStep2Complete(event) {
      this.createBooking(event.data)
    },
    async createBooking(data) {
      try {
        const booking = await this.$store.dispatch('booking/CREATE', data)

        this.$router.push(
          {
            name: routes.BOOKING_3,
            params: {
              bookingToken: booking.token
            }
          },
          // Wait until router resolves
          () => {
            // And wait until step3 has intialized
            this.$nextTick(() => {
              // Clear up the session storage, since we're done, so if the user
              // clicks back, the guard will redirect them to step1
              sessionStorage.removeItem('booking/temp-booking')
            })
          }
        )
      } catch (error) {
        this.$bus.$emit('CREATE_BOOKING_FAILED')

        this.$store.dispatch('toast/ERROR', error.message)
      }
    }
  }
}
</script>

<style lang="scss">
@import '~@/scss/variables';
@import '~@/halifax-ui/src/scss/framework';

/**
 * @param $num-items Number of columns in the row
 * @param $margin The outer margin to the left of the first item and to the right of the last item
 * @param $gutter Spacing BETWEEN each of the items
 */
// @function grid-item-width($num-items, $margin, $gutter) {
//   $computed-margin: 1 / $num-items * $margin;
//   $computed-gutter: ($num-items - 1) / $num-items * $gutter;
//   @return calc(
//     #{percentage(1 / ($num-items - 0.5))} - #{$computed-margin + $computed-gutter}
//   );
// }
@function grid-item-width($num-items, $margin, $gutter) {
  $computed-margin: $margin * 2 / $num-items;
  $computed-gutter: $gutter * ($num-items - 1) / $num-items;
  @return calc(#{percentage(1 / $num-items)} - #{$computed-margin + $computed-gutter});
}

.booking {
  $module: '.booking';
  $gutter: 10px;
  $margin: $padding-x-container;

  &__table-cta {
    margin-bottom: 46px;
  }
  &__summary-dl {
    dd::first-letter {
      text-transform: uppercase;
    }
  }
  &__fieldset {
    &:not(:last-of-type) {
      margin-bottom: 30px;
    }
  }
  &__fieldset-legend {
    margin-bottom: 20px;
  }
  &__fieldset-heading {
    font-size: 1.6rem;
    text-align: center;
  }
  &__fieldset-input + &__fieldset-input {
    margin-top: 10px;
  }
  &__fieldset-checkbox {
    margin-top: 20px;
  }
  &__button-group {
    &:not(:first-child) {
      margin-top: 50px;
    }
  }
  &__spinner {
    display: flex;

    #{$module}__fieldset--restaurant & {
      .touch & {
        min-height: 60px * 1 + 5px * 1;
      }
      .no-touch & {
        min-height: 60px * 3 + 10px * 3;
      }
    }
    #{$module}__fieldset--guests & {
      .touch & {
        min-height: 60px * 1 + 5px * 1;
      }
      .no-touch & {
        min-height: 60px * 2 + 10px * 2;
      }
    }
    #{$module}__fieldset--day & {
      .touch & {
        min-height: 60px * 1 + 5 * 1;
      }
      .no-touch & {
        min-height: 60px * 3 + 10 * 3;
      }
    }
    #{$module}__fieldset--times & {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba($background-color, 0.75);
      margin-top: -10px;
    }
  }
  &__slider {
    margin-top: -5px;

    .no-touch & .hui-slider__items-inner,
    #{$module}__fieldset--times & .hui-slider__items-inner {
      white-space: normal;
    }
    .hui-slider__items-inner {
      margin-top: 15px;
      margin-bottom: 20px;
    }
  }
  &__next {
    span:last-of-type:not(:only-child) {
      .window--framed & {
        @include below-desktop {
          display: none;
        }
      }
    }
  }
  &__show-more-button {
    text-align: center;
    display: block;
    margin-left: auto;
    margin-right: auto;
    line-height: 60px;
    height: 60px;
    text-decoration: underline;
    font-weight: 700;
    opacity: 0.5;
    cursor: pointer;
    background: transparent;
    border: none;
  }
  &__slider-placeholder {
    font-size: 1.2rem;
    opacity: 0.5;
    text-align: center;
    display: block;
    line-height: 65px;
    font-weight: 600;
  }
  &__feedback {
    font-size: 1.2rem;
    text-align: center;
    display: block;
    font-weight: 600;
    color: $halifax-red;

    a {
      white-space: nowrap;
    }
  }
  .hui-radio-button label {
    @include tablet {
      .no-touch & {
        height: 66px;
      }
    }
  }
  &__slide {
    margin-left: 5px;
    margin-right: 5px;
    padding-top: 5px;

    .no-touch & {
      padding-top: 10px;
    }
    &--show-more {
      font-size: 1.2rem;

      .no-touch & {
        margin-left: 0 !important;
        margin-right: 0 !important;
        width: 100% !important;
      }
    }
    @include first-of-type {
      margin-left: $padding-x-container;
    }
    @include last-of-type {
      margin-right: $padding-x-container;
    }
    #{$module}__fieldset--restaurant & {
      width: grid-item-width(2, $padding-x-container, $gutter);

      .no-touch & {
        width: grid-item-width(3, $padding-x-container, $gutter);

        @include every-nth-of-type(3, -2) {
          margin-left: $padding-x-container;
        }
      }
    }
    #{$module}__fieldset--guests & {
      width: grid-item-width(5, $padding-x-container, $gutter);

      .no-touch & {
        width: grid-item-width(9, $padding-x-container, $gutter);

        @include every-nth-of-type(9, -8) {
          margin-left: $padding-x-container;
        }
      }
    }
    #{$module}__fieldset--times & {
      .touch & {
        padding-top: 10px;
        width: grid-item-width(4, $padding-x-container, $gutter);

        @include every-nth-of-type(4, -3) {
          margin-left: $padding-x-container;
        }
        .hui-radio-button label {
          height: 40px;
        }
      }
      .no-touch & {
        width: grid-item-width(8, $padding-x-container, $gutter);

        @include every-nth-of-type(8, -7) {
          margin-left: $padding-x-container;
        }
      }
    }
    #{$module}__fieldset--day & {
      width: grid-item-width(3, $padding-x-container, $gutter);

      .no-touch & {
        width: grid-item-width(7, $padding-x-container, $gutter);

        @include every-nth-of-type(7, -6) {
          margin-left: $padding-x-container;
        }
      }
    }
    &--day-in-the-past {
      .touch & {
        width: 0;
        overflow: hidden;
        margin-right: -5px;
        pointer-events: none;
      }
    }
  }
  &__slide-today-label {
    display: block;
    height: 0;
    width: 100%;
    position: relative;
    text-align: center;

    &::after {
      content: attr(data-label);
      display: inline-block;
      font-size: 1.2rem;
      transform: translateY(-7px) translateY(-2px);
      background-color: $background-color;
      padding: 2px 4px;
      font-weight: 700;
      border-radius: 3px;
      line-height: 14px;
      height: 14px;
      box-sizing: content-box;
      color: $background-petrol-30;
    }
  }
  &__summary-dl-address {
    display: block;
    margin-top: 1px;
  }
}
</style>
